import type { Timestamp } from 'firebase/firestore'
import type { WorkspaceItemReference } from '../components/Common/WorkspaceItemPicker'

// ============================================================================
// Overview Data Model v2 (R29 §4 + R31 CHART block amendment)
// ============================================================================

export type OverviewBuiltinKey =
  | 'HEADER'
  | 'PULSE'
  | 'PEOPLE'
  | 'ATTENTION'
  | 'GOALS'
  | 'ACTIVITY'
  | 'MODULES'

export const OVERVIEW_BUILTIN_KEYS: readonly OverviewBuiltinKey[] = [
  'HEADER',
  'PULSE',
  'PEOPLE',
  'ATTENTION',
  'GOALS',
  'ACTIVITY',
  'MODULES',
] as const

export type OverviewSectionRef =
  | { kind: 'BUILTIN'; key: OverviewBuiltinKey; hidden?: boolean }
  | { kind: 'CUSTOM'; id: string; hidden?: boolean }

export type OverviewBlockWidth = 'full' | 'half' | 'third'

export type OverviewCalloutTone = 'note' | 'warning' | 'win'
export type OverviewImageRatio = '16:9' | '4:3' | '1:1' | '21:6'
export type OverviewEmbedRatio = '16:9' | '4:3'
export type OverviewChartHeight = 'S' | 'M' | 'L'

export type OverviewBlock = { id: string; width: OverviewBlockWidth } & (
  | { type: 'TEXT'; markdown: string }
  | { type: 'CALLOUT'; tone: OverviewCalloutTone; title?: string; body: string }
  | {
      type: 'IMAGE'
      source: { uploadId: string } | { url: string }
      alt: string
      ratio: OverviewImageRatio
    }
  | { type: 'EMBED'; url: string; ratio: OverviewEmbedRatio }
  | {
      type: 'LINKS'
      items: Array<{ ref: WorkspaceItemReference } | { url: string; label: string }>
    }
  | { type: 'PEOPLE'; items: Array<{ uid: string; note?: string }> }
  | { type: 'CHART'; chartId: string; height: OverviewChartHeight }
)

export type OverviewCustomSection = {
  id: string
  title: string
  blocks: OverviewBlock[]
}

export type OverviewDocumentV2 = {
  id: string
  schemaVersion: 2
  brief: string
  sections: OverviewSectionRef[]
  customSections: OverviewCustomSection[]
  createdBy: string
  createdByName: string
  createdAt: Timestamp | null
}

export type OverviewEditingLock = {
  userId: string
  userName: string
  acquiredAt: Timestamp | null
}

export const OVERVIEW_VERSION_RETENTION = 5
export const OVERVIEW_LOCK_TTL_MS = 2 * 60 * 1000
export const OVERVIEW_LOCK_HEARTBEAT_MS = 30 * 1000

export const OVERVIEW_V2_LIMITS = {
  MAX_CUSTOM_SECTIONS: 12,
  MAX_BLOCKS_PER_SECTION: 12,
  MAX_TEXT_CHARS: 4000,
  MAX_CALLOUT_CHARS: 400,
} as const

export const OVERVIEW_EMBED_HOST_ALLOWLIST: readonly string[] = [
  'youtube.com',
  'www.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
  'vimeo.com',
  'www.loom.com',
  'loom.com',
  'www.figma.com',
  'figma.com',
  'docs.google.com',
]

// ============================================================================
// overviewV1Legacy — O3.a/O4 deleted the panel and O5 removes the rest
// ============================================================================

export type OverviewMode = 'PUBLIC' | 'TEAM'

export type OverviewStyleVariant = 'PRISM' | 'NOIR' | 'SIGNAL' | 'PAPER'

export type OverviewSectionKind =
  | 'HERO'
  | 'PULSE'
  | 'GOALS'
  | 'MILESTONES'
  | 'TASKS'
  | 'CHECKLISTS'
  | 'MEMBERS'
  | 'RESOURCES'
  | 'FORMS'
  | 'BUDGET'
  | 'RISK'
  | 'ACTIVITY'
  | 'ANNOUNCEMENT'
  | 'RICH_TEXT'
  | 'IMAGE'
  | 'EMBED'
  | 'DIVIDER'
  | 'SPACER'
  | 'HEATMAP'
  | 'MEMBER_LOAD'
  | 'TYPE_MIX'
  | 'CALENDAR_STRIP'
  | 'PROGRESS_TREND'
  | 'BURNDOWN'
  | 'BUDGET_BREAKDOWN'
  | 'RISK_MATRIX'

export type OverviewSectionSize = {
  x: number
  y: number
  w: number
  h: number
}

export type OverviewSectionDensity = 'cozy' | 'compact' | 'spacious'
export type OverviewSectionBackgroundStyle = 'variant' | 'flat' | 'tint' | 'outline' | 'glass'

export type OverviewSectionVisual = {
  accentColor?: string
  iconOverride?: string
  showHeader?: boolean
  density?: OverviewSectionDensity
  backgroundStyle?: OverviewSectionBackgroundStyle
}

export type OverviewSectionSortKey = 'priority' | 'progress' | 'endDate' | 'title' | 'updatedAt' | 'weight'
export type OverviewSectionSortDir = 'asc' | 'desc'
export type OverviewSectionStatusFilter = 'TODO' | 'IN_PROGRESS' | 'BLOCKED' | 'COMPLETE'
export type OverviewSectionGroupBy = 'none' | 'type' | 'status' | 'assignee'

export type OverviewSectionData = {
  sortKey?: OverviewSectionSortKey
  sortDir?: OverviewSectionSortDir
  statusFilter?: OverviewSectionStatusFilter[]
  timeframeDays?: number
  groupBy?: OverviewSectionGroupBy
}

export type OverviewMediaAspectRatio = '16:9' | '4:3' | '1:1' | 'auto'

export type OverviewSectionMedia = {
  markdown?: string
  imageUrl?: string
  imageAlt?: string
  embedUrl?: string
  aspectRatio?: OverviewMediaAspectRatio
}

export type OverviewAnalyticsSource = 'DASHBOARD' | 'TASKS' | 'GOALS'

export type OverviewSectionAnalytics = {
  source?: OverviewAnalyticsSource
  windowDays?: number
  windowWeeks?: number
  targetId?: string
}

export type OverviewSectionConfig = {
  title?: string
  description?: string
  targetIds?: string[]
  resourceLinks?: Array<{ id: string; label: string; url: string }>
  limit?: number
  visual?: OverviewSectionVisual
  data?: OverviewSectionData
  media?: OverviewSectionMedia
  analytics?: OverviewSectionAnalytics
}

export type OverviewSection = {
  id: string
  kind: OverviewSectionKind
  title: string
  variant: OverviewStyleVariant
  layout: OverviewSectionSize
  config: OverviewSectionConfig
  hidden?: boolean
}

export type OverviewPageModeState = {
  sections: OverviewSection[]
  shelf: OverviewSection[]
}

export type OverviewPageRadius = 'sharp' | 'soft' | 'pill'
export type OverviewPageFontPair = 'system' | 'grotesk' | 'serif' | 'mono-display'
export type OverviewPageBackgroundPattern =
  | 'none'
  | 'grid'
  | 'dots'
  | 'noise'
  | 'gradient'
  | 'aurora'
  | 'scanline'
  | 'mesh'
  | 'blueprint'
  | 'hexgrid'
  | 'depth'

export type OverviewPageTheme = {
  accentColor?: string
  radius?: OverviewPageRadius
  fontPair?: OverviewPageFontPair
  backgroundPattern?: OverviewPageBackgroundPattern
}

export type OverviewDocument = {
  id: string
  modes: Record<OverviewMode, OverviewPageModeState>
  theme?: OverviewPageTheme
  createdBy: string
  createdByName: string
  createdAt: Timestamp | null
}

export const OVERVIEW_GRID_COLUMNS = 12

export const OVERVIEW_SECTION_LIMITS: Record<OverviewSectionKind, number> = {
  HERO: 1,
  PULSE: 1,
  MEMBERS: 1,
  ACTIVITY: 1,
  BUDGET: 1,
  RISK: 1,
  FORMS: 2,
  GOALS: 4,
  MILESTONES: 4,
  TASKS: 4,
  CHECKLISTS: 4,
  RESOURCES: 4,
  ANNOUNCEMENT: 3,
  RICH_TEXT: 6,
  IMAGE: 4,
  EMBED: 2,
  DIVIDER: 6,
  SPACER: 6,
  HEATMAP: 2,
  MEMBER_LOAD: 1,
  TYPE_MIX: 1,
  CALENDAR_STRIP: 1,
  PROGRESS_TREND: 2,
  BURNDOWN: 2,
  BUDGET_BREAKDOWN: 1,
  RISK_MATRIX: 1,
}

export const TEAM_ONLY_OVERVIEW_SECTIONS: OverviewSectionKind[] = [
  'BUDGET',
  'RISK',
  'ACTIVITY',
  'MEMBER_LOAD',
  'BURNDOWN',
  'BUDGET_BREAKDOWN',
  'RISK_MATRIX',
]

